import type { protocol } from "@codesandbox/pitcher-client";
import type { TabParams } from "features/types/tabs";
import type { Task } from "features/types/task";
import type { IRange } from "monaco-editor";

export const getOpenPreviewDevtoolsAction = (
  port: number,
  tasks: Task[],
  unassignedPorts: protocol.port.Port[],
): {
  params: TabParams;
  port: protocol.port.Port;
} | null => {
  if (Number.isNaN(port)) {
    return null;
  }

  const unassignedPort = unassignedPorts.find((p) => p.port === port);
  if (unassignedPort) {
    return {
      params: {
        type: "UNASSIGNED_PORT",
        port: unassignedPort.port,
      },
      port: unassignedPort,
    };
  }

  const task = tasks.find((task) => task.ports.some((p) => p.port === port));
  if (task) {
    const portDefinition = task.ports.find((p) => p.port === port)!;
    return {
      port: portDefinition,
      params: {
        type: "TASK_PORT",
        taskId: task.id,
        port: port,
      },
    };
  }

  return null;
};

/**
 * You can show code in different languages, so we wrap the code in the
 * relevant language.
 */
export const getCodeRunCommand = (
  code: string,
  languageType: string,
): string | undefined => {
  if (languageType === "language-js") {
    return `node -p "${code.trim()}"`;
  }

  if (languageType === "language-norun") {
    return undefined;
  }

  return code.trim();
};

/**
 * Parses selections in the format of e.g. `100` or `100-102`. And returns
 * the start and end line number.
 */
export function parseSelections(selectionsRaw?: string): undefined | IRange[] {
  if (!selectionsRaw) {
    return;
  }

  const [start, end] = selectionsRaw.split("-");

  const parsedStart = Number.parseInt(start!);
  if (!Number.isFinite(parsedStart)) {
    return;
  }

  const startLine = parsedStart;
  let endLine: number | undefined = undefined;
  if (end) {
    const parsedEnd = Number.parseInt(end);

    if (Number.isFinite(parsedEnd)) {
      endLine = parsedEnd;
    }
  }

  return [
    {
      startLineNumber: startLine,
      startColumn: 0,
      endLineNumber: endLine ?? startLine,
      endColumn: 99999,
    },
  ];
}

/**
 * This changes the command to something that we can autodetect
 */
export function sanitizeCommand(
  command: string | undefined,
): string | undefined {
  if (command === undefined) {
    return undefined;
  }

  if (command === "yarn") {
    return "yarn install";
  }

  if (command.startsWith("yarn run")) {
    return command.replace("yarn run", "yarn");
  }

  return command;
}

export const codeStyle = {
  'code[class*="language-"]': {
    color: "#ffffff",
    fontFamily:
      "FiraCode, SFMono-Regular, Consolas, 'Liberation Mono', Menlo, Courier, monospace",
    fontSize: "1em",
    textAlign: "left",
    whiteSpace: "pre",
    wordSpacing: "normal",
    wordBreak: "normal",
    wordWrap: "normal",
    lineHeight: "1.5",
    MozTabSize: "4",
    OTabSize: "4",
    tabSize: "4",
    WebkitHyphens: "none",
    MozHyphens: "none",
    msHyphens: "none",
    hyphens: "none",
  },
  'pre[class*="language-"]': {
    color: "#ffffff",
    fontFamily:
      "FiraCode, SFMono-Regular, Consolas, 'Liberation Mono', Menlo, Courier, monospace",
    fontSize: "1em",
    textAlign: "left",
    whiteSpace: "pre",
    wordSpacing: "normal",
    wordBreak: "normal",
    wordWrap: "normal",
    lineHeight: "1.5",
    MozTabSize: "4",
    OTabSize: "4",
    tabSize: "4",
    WebkitHyphens: "none",
    MozHyphens: "none",
    msHyphens: "none",
    hyphens: "none",
    overflow: "auto",
    borderRadius: ".5em",
  },
  comment: {
    color: "#6f6f6f",
  },
  prolog: {
    color: "#6f6f6f",
  },
  doctype: {
    color: "#6f6f6f",
  },
  cdata: {
    color: "#6f6f6f",
  },
  punctuation: {
    color: "#86897a",
  },
  namespace: {
    color: "#cdf861",
  },
  tag: {
    color: "#a390ff",
  },
  boolean: {
    color: "#7ad9fb",
  },
  number: {
    color: "#7ad9fb",
  },
  deleted: {
    color: "red",
  },
  keyword: {
    color: "#a390ff",
  },
  property: {
    color: "#cdf861",
  },
  selector: {
    color: "red",
  },
  constant: {
    color: "#a390ff",
  },
  symbol: {
    color: "hsl(53, 89%, 79%)",
  },
  builtin: {
    color: "hsl(53, 89%, 79%)",
  },
  "attr-name": {
    color: "#a390ff",
  },
  "attr-value": {
    color: "#cabeff",
  },
  string: {
    color: "#bfd084",
  },
  char: {
    color: "#bfd084",
  },
  operator: {
    color: "#7ad9fb",
  },
  entity: {
    color: "hsl(76, 21%, 52%)",
    cursor: "help",
  },
  url: {
    color: "hsl(76, 21%, 52%)",
  },
  ".language-css .token.string": {
    color: "hsl(76, 21%, 52%)",
  },
  ".style .token.string": {
    color: "hsl(76, 21%, 52%)",
  },
  variable: {
    color: "hsl(76, 21%, 52%)",
  },
  inserted: {
    color: "hsl(76, 21%, 52%)",
  },
  atrule: {
    color: "hsl(218, 22%, 55%)",
  },
  regex: {
    color: "hsl(42, 75%, 65%)",
  },
  important: {
    color: "hsl(42, 75%, 65%)",
    fontWeight: "bold",
  },
  bold: {
    fontWeight: "bold",
  },
  italic: {
    fontStyle: "italic",
  },
  ".language-markup .token.tag": {
    color: "hsl(33, 33%, 52%)",
  },
  ".language-markup .token.attr-name": {
    color: "hsl(33, 33%, 52%)",
  },
  ".language-markup .token.punctuation": {
    color: "hsl(33, 33%, 52%)",
  },
  "": {
    position: "relative",
    zIndex: "1",
  },
  ".line-highlight.line-highlight": {
    background:
      "linear-gradient(to right, hsla(0, 0%, 33%, .1) 70%, hsla(0, 0%, 33%, 0))",
    borderBottom: "1px dashed hsl(0, 0%, 33%)",
    borderTop: "1px dashed hsl(0, 0%, 33%)",
    marginTop: "0.75em",
    zIndex: "0",
  },
  ".line-highlight.line-highlight:before": {
    backgroundColor: "hsl(215, 15%, 59%)",
    color: "hsl(24, 20%, 95%)",
  },
  ".line-highlight.line-highlight[data-end]:after": {
    backgroundColor: "hsl(215, 15%, 59%)",
    color: "hsl(24, 20%, 95%)",
  },
};
